import { chain, compact, first, isEmpty } from 'lodash';
import { GcfComponentSectionProps } from 'src/components/gcfComponentSection';
import {
  type IGcfProductList,
  type IGcfProductListProduct,
  type IGcfProductListProducts,
  ComponentTypes,
  DynamicSectionEvents,
  IAssemblyComponent,
  IAsset,
  ICCAsset,
  IProduct,
  IScreen,
} from 'src/interfaces';

import { getAssetUrl } from './environment';
import { extractProductsFromGCFGroups } from './product';

export enum GcfMockData {
  COD_CUSTOMER_KNOWN = 'COD_CUSTOMER_KNOWN',
  COD_CUSTOMER_KNOWN_MOP = 'COD_CUSTOMER_KNOWN_MOP',
  COD_CUSTOMER_UNKNOWN = 'COD_CUSTOMER_UNKNOWN',
  COD_PAGINATED = 'COD_PAGINATED',
  COD_EXPANDED = 'COD_EXPANDED',
  COD_EXPANDED_WITH_SMALL_BANNER = 'COD_EXPANDED_WITH_SMALL_BANNER',
  COD_CUSTOMER_KNOWN_UNLINKED_PAYMENT = 'COD_CUSTOMER_KNOWN_UNLINKED_PAYMENT',
  DY_SUGGESTIONS_OPEN = 'DY_SUGGESTIONS_OPEN',
  DY_SUGGESTIONS_CLOSE = 'DY_SUGGESTIONS_CLOSE',
  DY_RECOMMENDATIONS_UPDATE = 'DY_RECOMMENDATIONS_UPDATE',
}

export const getSectionPreviewData = (
  componentType: ComponentTypes,
  event: string = 'default',
  orderType: string = 'default',
): { event: string; mockData: string | null } | null => {
  const componentSectionEvents = {
    [ComponentTypes.SINGLE_SHELF]: {
      default: DynamicSectionEvents.DY_RECOMMENDATIONS_UPDATE,
      [DynamicSectionEvents.DY_SUGGESTIONS_OPEN]:
        DynamicSectionEvents.DY_SUGGESTIONS_OPEN,
      [DynamicSectionEvents.DY_SUGGESTIONS_CLOSE]:
        DynamicSectionEvents.DY_SUGGESTIONS_CLOSE,
      [DynamicSectionEvents.DY_RECOMMENDATIONS_UPDATE]:
        DynamicSectionEvents.DY_RECOMMENDATIONS_UPDATE,
    },
    [ComponentTypes.COD]: {
      default: DynamicSectionEvents.COD_ORDER_OPEN,
      [DynamicSectionEvents.COD_COMPLETE]: DynamicSectionEvents.COD_COMPLETE,
      [DynamicSectionEvents.COD_ORDER_OPEN]:
        DynamicSectionEvents.COD_ORDER_OPEN,
      [DynamicSectionEvents.COD_ORDER_EXPAND]:
        DynamicSectionEvents.COD_ORDER_EXPAND,
      [DynamicSectionEvents.COD_CONFIRMATION_OPEN]:
        DynamicSectionEvents.COD_CONFIRMATION_OPEN,
    },
    [ComponentTypes.COD_THANK_YOU]: {
      default: DynamicSectionEvents.COD_THANK_YOU_OPEN,
      [DynamicSectionEvents.COD_THANK_YOU_OPEN]:
        DynamicSectionEvents.COD_THANK_YOU_OPEN,
      [DynamicSectionEvents.COD_THANK_YOU_CLOSE]:
        DynamicSectionEvents.COD_THANK_YOU_CLOSE,
    },
  };

  // TODO: update the mocks from here: https://git.coates.io/sb/content-platform/gcf-cod/-/merge_requests/100/diffs
  const componentMocks = {
    [ComponentTypes.SINGLE_SHELF]: {
      default: null,
      [GcfMockData.DY_SUGGESTIONS_OPEN]: GcfMockData.DY_SUGGESTIONS_OPEN,
      [GcfMockData.DY_SUGGESTIONS_CLOSE]: GcfMockData.DY_SUGGESTIONS_CLOSE,
      [GcfMockData.DY_RECOMMENDATIONS_UPDATE]:
        GcfMockData.DY_RECOMMENDATIONS_UPDATE,
    },
    [ComponentTypes.COD]: {
      default: GcfMockData.COD_CUSTOMER_UNKNOWN,
      [GcfMockData.COD_CUSTOMER_KNOWN]: GcfMockData.COD_CUSTOMER_KNOWN,
      [GcfMockData.COD_CUSTOMER_KNOWN_MOP]: GcfMockData.COD_CUSTOMER_KNOWN_MOP,
      [GcfMockData.COD_CUSTOMER_UNKNOWN]: GcfMockData.COD_CUSTOMER_UNKNOWN,
      [GcfMockData.COD_PAGINATED]: GcfMockData.COD_PAGINATED,
      [GcfMockData.COD_EXPANDED]: GcfMockData.COD_EXPANDED,
      [GcfMockData.COD_EXPANDED_WITH_SMALL_BANNER]:
        GcfMockData.COD_EXPANDED_WITH_SMALL_BANNER,
      [GcfMockData.COD_CUSTOMER_KNOWN_UNLINKED_PAYMENT]:
        GcfMockData.COD_CUSTOMER_KNOWN_UNLINKED_PAYMENT,
    },
    [ComponentTypes.COD_THANK_YOU]: {
      default: GcfMockData.COD_CUSTOMER_KNOWN,
      [GcfMockData.COD_CUSTOMER_KNOWN]: GcfMockData.COD_CUSTOMER_KNOWN,
      [GcfMockData.COD_CUSTOMER_UNKNOWN]: GcfMockData.COD_CUSTOMER_UNKNOWN,
    },
  };

  if (!(componentType in componentSectionEvents)) {
    return null;
  }

  const componentEvent =
    componentSectionEvents[
      componentType as keyof typeof componentSectionEvents
    ];
  const componentMock =
    componentMocks[componentType as keyof typeof componentMocks];

  return {
    event: componentEvent[event as keyof typeof componentEvent],
    mockData: componentMock[orderType as keyof typeof componentMock],
  };
};

export const getGCFSections = (
  assemblyComponents: IAssemblyComponent[] = [],
  screen: IScreen,
): GcfComponentSectionProps[] => {
  const assemblyComponentsByScreens = assemblyComponents.filter(
    (item) => item.layout_screen_id === screen.layoutScreenId,
  );

  // const combinedSections = uniqBy(assemblyComponentsByScreens, (item) =>
  //   [item.layout_screen_id, item.screen_section_id].join(),
  // );

  return screen.sections.map((section) => {
    const assemblyComponents = chain(assemblyComponentsByScreens)
      .filter((item) => item.screen_section_id === section.screenSectionId)
      .compact()
      .map((item) => ({
        ...(item.id != null || item.uid != null
          ? { id: item.id ?? item.uid }
          : {}),
        ...(item.component_id != null
          ? { componentId: item.component_id }
          : {}),
        component: {
          ...(item.component_id != null ? { id: item.component_id } : {}),
          name: item.component?.name,
          type: item.component?.component_type?.name,
          config: item.component?.config,
          width: item.component?.width,
          height: item.component?.height,
          theme: item.component?.theme?.name,
        },
        rotationOrder: item.rotation_order,
        backupOrder: item.backup_order,
        config: item.config,
      }))
      .value();

    return {
      components: assemblyComponents, // TODO: remove this later in i10
      assemblyComponents: assemblyComponents, // Components v1.2.0^
      height: section.height,
      width: section.width,
      left: section.left_offset,
      config: section.config,
      top: section.top_offset,
    };
  });
};

export const getGCFComponentScale = (
  componentWidth: number,
  componentHeight: number,
  fullWidth: boolean,
  containerWidth?: number,
  containerHeight?: number,
) => {
  let scale = 0.1;

  if (containerWidth && containerHeight && componentWidth && componentHeight) {
    const tempContainerWidth = containerWidth - 1;
    const tempContainerHeight = containerHeight - 1;

    const minWidthScale = Math.min(
      700 / componentWidth,
      tempContainerWidth / componentWidth,
    );
    const minHeightScale = Math.min(
      800 / componentHeight,
      tempContainerHeight / componentHeight,
    );

    const maxWidthScale = tempContainerWidth / componentWidth;
    const maxHeightScale = tempContainerHeight / componentHeight;
    const minScale = Math.min(minWidthScale, minHeightScale);
    const maxScale = Math.min(maxHeightScale, maxWidthScale);

    scale = Math.floor((fullWidth ? maxScale : minScale) * 1000) / 1000;
  }

  return scale;
};

export const getThumbnailSrc = (assetName?: string) => {
  if (!assetName) return '';

  return getAssetUrl(`/_assets/129x129/${assetName}.png`);
};

export const getPreviewUrlSrc = (assetName?: string) => {
  if (!assetName) return '';

  return getAssetUrl(`/file/${assetName}`);
};

export const cleanQuillEmptyContent = (html: string) => {
  if (!html) return '';

  // Remove all HTML tags and check if there's any meaningful content
  const textOnly = html.replace(/<[^>]*>/g, '').trim();

  // Also remove common HTML entities
  const cleanText = textOnly.replace(/&nbsp;/g, '').replace(/\s/g, '');
  if (cleanText === '') {
    return '';
  }

  return html;
};

export const getProductLineupLabel = (
  gcfGroupProduct: IGcfProductListProduct,
  product?: IProduct,
) => {
  const variations = product?.productVariations ?? [];
  const productVariation = variations.find(
    (variation) => variation.product_code === product?.product_code,
  );

  const name =
    cleanQuillEmptyContent(gcfGroupProduct.config.nameOverride) || // lineup override
    product?.name_override || // product name override
    productVariation?.name ||
    getProductName(first(product?.assets)?.name || '') || // product asset name
    'Product'; //default

  return `${name}`;
};

export const getProductNameFromAssetId = (
  assets: ICCAsset[],
  assetId?: number,
) => {
  const assetRecord = getAssetFromAssetId(assets, assetId);

  return assetRecord?.name?.split('.').slice(0, -1).join('.');
};

export const getProductName = (name: string) => {
  return name.split('.').slice(0, -1).join('.');
};

export const getAssetFromAssetId = (assets: ICCAsset[], assetId?: number) => {
  return assets?.find((asset) => asset.id === assetId);
};

export const getSBAssetFromCCAsset = (assets: IAsset[], assetName?: string) => {
  return assets?.find((asset) => asset.fileName === assetName);
};

/**
 * Filters out GCF products based on their presence in the provided product categories.
 *
 * @param products - An array of the products from specific product categories to filter GCF products.
 * @param productList - The GCF productList to filter.
 * @param keepAllWhenEmpty - Behaviour when `products` is empty: `true` (default) keeps
 *  all GCF products (an unrestricted component has no category filter to apply);
 *  `false` removes them all (a category restriction that matches no products must
 *  drop every incompatible product rather than keep them).
 * @returns
 *  - productList: A new GCF productList with filtered products and backups.
 *  - productVariationCodes: A unique set of product variation codes from the filtered GCF productList.
 */
export const filterGcfProducts = (
  products: IProduct[],
  productList?: IGcfProductList,
  keepAllWhenEmpty = true,
) => {
  const { products: gcfProducts, backups: gcfBackups } =
    extractProductsFromGCFGroups(productList?.groups);

  const productFilter = (item: IGcfProductListProduct) =>
    isEmpty(products)
      ? keepAllWhenEmpty
      : products.some((product) => product.product_code === item.productCode);

  const combinedGcfProducts = [...gcfProducts, ...gcfBackups].filter(
    productFilter,
  );

  const filterGroupEntries = (entries: IGcfProductListProducts = {}) =>
    Object.fromEntries(
      Object.entries(entries).filter(([_, value]) => productFilter(value)),
    );

  const filteredGroups = productList?.groups
    ? Object.fromEntries(
        Object.entries(productList.groups).map(([key, group]) => [
          key,
          {
            ...group,
            products: filterGroupEntries(group.products),
            backups: filterGroupEntries(group.backups),
          },
        ]),
      )
    : {};

  const productVariationCodes = Array.from(
    new Set(
      combinedGcfProducts.flatMap((item) => {
        const productVariationCodes = Object.values(item.sizes ?? {}).flatMap(
          (sizeValue) =>
            Object.values(sizeValue.variations).map(
              (flavorValue) => flavorValue.productCode,
            ),
        );
        const bundleImageCodes =
          item.bundleImages?.map((bundle) => bundle.productCode) || [];

        return compact([...bundleImageCodes, ...productVariationCodes]);
      }),
    ),
  );

  return {
    productList: { ...productList, groups: filteredGroups },
    productVariationCodes,
  };
};
